// frontend/src/components/Dashboard/TestimonialManager.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  getUserTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  getAllTestimonials,
  getTestimonialStats,
  approveTestimonial,
  rejectTestimonial,
  featureTestimonial,
  adminRespondToTestimonial,
  markTestimonialHelpful,
} from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faStarHalfAlt,
  faEdit,
  faTrash,
  faPlus,
  faTimes,
  faCheck,
  faQuoteLeft,
  faUser,
  faTractor,
  faStore,
  faThumbsUp,
  faThumbsDown,
  faReply,
  faThumbtack,
  faClock,
  faSpinner,
  faFilter,
  faSearch,
  faExclamationTriangle,
  faCheckCircle,
  faEye,
  faEyeSlash,
  faSort,
  faSortUp,
  faSortDown,
  faCalendarAlt,
  faUserCircle,
  faEnvelope,
  faPhone,
  faBox,
  faTag,
  faInfoCircle,
  faReplyAll,
  faGlobe,
  faShip,
  faAnchor,
  faBuilding,
  faLeaf,
  faCow,
  faRocket,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import Loader from "../UI/Loader";

// ============================================
// BRAND & SUPPLIER CONSTANTS (Globally Sourced)
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// CONFIDENTIAL SUPPLIER - Displayed as "Globally Sourced"
// ============================================
const SUPPLIER = {
  name: "Globally Sourced",
  country: "Globally Sourced",
  city: "Global Supply Chain",
  portLoading: "Global Supply Chain",
  portDischarge: "Freetown, Sierra Leone",
};

// ============================================
// HELPERS
// ============================================
const getStatusBadge = (status) => {
  const styles = {
    pending: {
      bg: "#fef3c7",
      color: "#d97706",
      text: "⏳ Pending Review",
      icon: faClock,
    },
    approved: {
      bg: "#dcfce7",
      color: "#16a34a",
      text: "✅ Approved",
      icon: faCheck,
    },
    rejected: {
      bg: "#fee2e2",
      color: "#dc2626",
      text: "❌ Rejected",
      icon: faTimes,
    },
  };
  return styles[status] || styles.pending;
};

// ============================================
// RATING STAR COMPONENT - FIXED
// ============================================
const RatingStar = ({ rating }) => {
  const fullStars = Math.floor(rating || 0);
  const hasHalfStar = (rating || 0) - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <span
      className="rating-stars"
      style={{ display: "inline-flex", alignItems: "center", gap: "2px" }}
    >
      {[...Array(Math.max(0, fullStars))].map((_, i) => (
        <FontAwesomeIcon
          key={`full-${i}`}
          icon={faStar}
          style={{ color: BRAND.secondary, fontSize: "0.9rem" }}
        />
      ))}
      {hasHalfStar && (
        <FontAwesomeIcon
          icon={faStarHalfAlt}
          style={{ color: BRAND.secondary, fontSize: "0.9rem" }}
        />
      )}
      {[...Array(Math.max(0, emptyStars))].map((_, i) => (
        <FontAwesomeIcon
          key={`empty-${i}`}
          icon={faStar}
          style={{ color: "#e5e5e5", fontSize: "0.9rem" }}
        />
      ))}
    </span>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const TestimonialManager = ({ isAdmin = true }) => {
  const { user } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [formData, setFormData] = useState({
    rating: 5,
    title: "",
    content: "",
    productName: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [viewMode, setViewMode] = useState("grid");

  // ============================================
  // FETCH TESTIMONIALS
  // ============================================
  const fetchTestimonials = useCallback(async () => {
    setLoading(true);
    try {
      let testimonialsData = [];
      let statsData = {};

      if (isAdmin) {
        try {
          const params = { status: filter };
          if (searchTerm) {
            params.search = searchTerm;
          }
          const testimonialsRes = await getAllTestimonials(params);
          testimonialsData =
            testimonialsRes?.data?.testimonials || testimonialsRes?.data || [];
        } catch (err) {
          console.error("Error fetching admin testimonials:", err);
          testimonialsData = [];
        }

        try {
          const statsRes = await getTestimonialStats();
          statsData = statsRes?.data || {};
        } catch (err) {
          console.error("Error fetching testimonial stats:", err);
          statsData = {};
        }
      } else {
        try {
          const testimonialsRes = await getUserTestimonials();
          testimonialsData =
            testimonialsRes?.data?.testimonials || testimonialsRes?.data || [];
        } catch (err) {
          console.error("Error fetching user testimonials:", err);
          testimonialsData = [];
        }
      }

      // Ensure all testimonials have origin set to "Globally Sourced"
      const processedTestimonials = (
        Array.isArray(testimonialsData) ? testimonialsData : []
      ).map((t) => ({
        ...t,
        origin: "Globally Sourced",
      }));

      setTestimonials(processedTestimonials);
      setStats(statsData);
    } catch (error) {
      console.error("Error in fetchTestimonials:", error);
      setTestimonials([]);
      setStats({});
      setMessage({ type: "error", text: "Failed to load testimonials" });
    } finally {
      setLoading(false);
    }
  }, [filter, searchTerm, isAdmin]);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: "", text: "" });

    try {
      if (editingTestimonial) {
        await updateTestimonial(editingTestimonial._id, formData);
        setMessage({
          type: "success",
          text: "Testimonial updated successfully!",
        });
      } else {
        await createTestimonial(formData);
        setMessage({
          type: "success",
          text: "Thank you! Your testimonial will be reviewed.",
        });
      }
      setShowModal(false);
      resetForm();
      fetchTestimonials();
    } catch (error) {
      console.error("Error saving testimonial:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to save testimonial",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this testimonial?")) {
      try {
        await deleteTestimonial(id);
        fetchTestimonials();
        setMessage({
          type: "success",
          text: "Testimonial deleted successfully",
        });
      } catch (error) {
        console.error("Error deleting testimonial:", error);
        setMessage({ type: "error", text: "Failed to delete testimonial" });
      }
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveTestimonial(id);
      fetchTestimonials();
      setMessage({ type: "success", text: "Testimonial approved!" });
    } catch (error) {
      console.error("Error approving testimonial:", error);
      setMessage({ type: "error", text: "Failed to approve testimonial" });
    }
  };

  const handleReject = async (id) => {
    const reason = prompt("Please provide a reason for rejection:");
    if (reason !== null) {
      try {
        await rejectTestimonial(id, { rejectionReason: reason });
        fetchTestimonials();
        setMessage({ type: "success", text: "Testimonial rejected" });
      } catch (error) {
        console.error("Error rejecting testimonial:", error);
        setMessage({ type: "error", text: "Failed to reject testimonial" });
      }
    }
  };

  const handleFeature = async (id) => {
    try {
      await featureTestimonial(id);
      fetchTestimonials();
      setMessage({ type: "success", text: "Testimonial featured!" });
    } catch (error) {
      console.error("Error featuring testimonial:", error);
      setMessage({ type: "error", text: "Failed to feature testimonial" });
    }
  };

  const handleAdminResponse = async () => {
    if (!responseText.trim()) {
      setMessage({ type: "error", text: "Please enter a response" });
      return;
    }
    try {
      await adminRespondToTestimonial(selectedTestimonial._id, responseText);
      setShowResponseModal(false);
      setResponseText("");
      setSelectedTestimonial(null);
      fetchTestimonials();
      setMessage({ type: "success", text: "Response sent successfully!" });
    } catch (error) {
      console.error("Error sending response:", error);
      setMessage({ type: "error", text: "Failed to send response" });
    }
  };

  const handleMarkHelpful = async (id) => {
    try {
      await markTestimonialHelpful(id);
      fetchTestimonials();
    } catch (error) {
      console.error("Error marking helpful:", error);
      setMessage({ type: "error", text: "Failed to mark as helpful" });
    }
  };

  const resetForm = () => {
    setFormData({ rating: 5, title: "", content: "", productName: "" });
    setEditingTestimonial(null);
  };

  const openEditModal = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      rating: testimonial.rating || 5,
      title: testimonial.title || "",
      content: testimonial.content || "",
      productName: testimonial.productName || "",
    });
    setShowModal(true);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return faSort;
    return sortDirection === "asc" ? faSortUp : faSortDown;
  };

  const getStatusStats = () => {
    return [
      { label: "Total", value: stats.total || 0, color: "#3b82f6" },
      { label: "Pending", value: stats.pending || 0, color: "#f59e0b" },
      { label: "Approved", value: stats.approved || 0, color: "#16a34a" },
      { label: "Rejected", value: stats.rejected || 0, color: "#dc2626" },
      { label: "Featured", value: stats.featured || 0, color: "#8b5cf6" },
    ];
  };

  const sortedTestimonials = [...testimonials].sort((a, b) => {
    let aVal = a[sortField] || "";
    let bVal = b[sortField] || "";
    if (sortField === "rating" || sortField === "helpfulCount") {
      aVal = aVal || 0;
      bVal = bVal || 0;
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    }
    if (typeof aVal === "string") {
      return sortDirection === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
  });

  const userTypeIcon = user?.userType === "farmer" ? faTractor : faStore;

  // ============================================
  // RENDER
  // ============================================
  if (loading) return <Loader />;

  // Admin View
  if (isAdmin) {
    return (
      <div className="testimonial-manager admin">
        {/* Header */}
        <div className="section-header">
          <div className="header-left">
            <FontAwesomeIcon icon={faQuoteLeft} className="header-icon" />
            <h2>Testimonial Management</h2>
            <span className="supplier-badge">
              <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
            </span>
          </div>
          <div className="header-actions">
            <button
              className="btn-toggle-view"
              onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
            >
              <FontAwesomeIcon
                icon={viewMode === "grid" ? faEyeSlash : faEye}
              />
              {viewMode === "grid" ? "List View" : "Grid View"}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div
          className="admin-stats-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          {getStatusStats().map((stat, index) => (
            <div
              key={index}
              className="stat-item-card"
              style={{
                borderColor: stat.color,
                background: "#ffffff",
                padding: "0.75rem",
                borderRadius: "8px",
                borderLeft: `4px solid ${stat.color}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <div
                className="stat-value"
                style={{
                  color: stat.color,
                  fontSize: "1.25rem",
                  fontWeight: "700",
                }}
              >
                {stat.value}
              </div>
              <div
                className="stat-label"
                style={{ fontSize: "0.7rem", color: "#5A5A5A" }}
              >
                {stat.label}
              </div>
            </div>
          ))}
          {stats.averageRating > 0 && (
            <div
              className="stat-item-card rating"
              style={{
                background: "#ffffff",
                padding: "0.75rem",
                borderRadius: "8px",
                borderLeft: "4px solid #D9A321",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <div
                className="stat-value"
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "700",
                  color: "#D9A321",
                }}
              >
                {stats.averageRating.toFixed(1)}
                <span className="stat-star" style={{ marginLeft: "0.25rem" }}>
                  ⭐
                </span>
              </div>
              <div
                className="stat-label"
                style={{ fontSize: "0.7rem", color: "#5A5A5A" }}
              >
                Avg Rating
              </div>
            </div>
          )}
          <div
            className="stat-item-card global"
            style={{
              background: "linear-gradient(135deg, #006B2E, #014421)",
              padding: "0.75rem",
              borderRadius: "8px",
              color: "#ffffff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <div
              className="stat-value"
              style={{ fontSize: "1.25rem", fontWeight: "700" }}
            >
              <FontAwesomeIcon icon={faGlobe} />
            </div>
            <div
              className="stat-label"
              style={{ fontSize: "0.7rem", opacity: "0.8" }}
            >
              Globally Sourced
            </div>
          </div>
        </div>

        {/* Supplier Info - Globally Sourced */}
        <div
          className="supplier-info-card"
          style={{
            background: "linear-gradient(135deg, #006B2E, #014421)",
            padding: "0.75rem 1.25rem",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            color: "#ffffff",
            marginBottom: "1.5rem",
            boxShadow: "0 4px 16px rgba(0,107,46,0.3)",
          }}
        >
          <FontAwesomeIcon
            icon={faGlobe}
            className="supplier-icon"
            style={{ fontSize: "1.5rem", color: "#D9A321" }}
          />
          <div className="supplier-info" style={{ flex: "1" }}>
            <strong style={{ fontSize: "0.938rem" }}>
              Premium Products, Globally Sourced
            </strong>
            <span
              style={{ display: "block", fontSize: "0.7rem", opacity: "0.8" }}
            >
              <FontAwesomeIcon icon={faGlobe} /> Global Supply Chain &bull;
              <FontAwesomeIcon icon={faAnchor} /> Quality Assured
            </span>
          </div>
          <span
            className="supplier-badge"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.25rem",
              fontSize: "0.6rem",
              background: "rgba(217,163,33,0.2)",
              color: "#D9A321",
              padding: "0.1rem 0.5rem",
              borderRadius: "12px",
            }}
          >
            <FontAwesomeIcon icon={faCheckCircle} /> Quality Assured
          </span>
        </div>

        {/* Filters */}
        <div
          className="filters-bar"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            marginBottom: "1.25rem",
            alignItems: "center",
          }}
        >
          <div
            className="filters-left"
            style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
          >
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
              style={{
                padding: "0.4rem 0.75rem",
                border: "1px solid #e5e5e5",
                borderRadius: "8px",
                fontSize: "0.813rem",
                background: "#ffffff",
              }}
            >
              <option value="all">All Testimonials</option>
              <option value="pending">⏳ Pending</option>
              <option value="approved">✅ Approved</option>
              <option value="rejected">❌ Rejected</option>
            </select>
            <div
              className="search-wrapper"
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}
            >
              <FontAwesomeIcon
                icon={faSearch}
                className="search-icon"
                style={{
                  position: "absolute",
                  left: "0.75rem",
                  color: "#9ca3af",
                }}
              />
              <input
                type="text"
                placeholder="Search testimonials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
                style={{
                  padding: "0.4rem 0.75rem 0.4rem 2.25rem",
                  border: "1px solid #e5e5e5",
                  borderRadius: "8px",
                  fontSize: "0.813rem",
                  width: "200px",
                  background: "#ffffff",
                }}
              />
            </div>
          </div>
          <div
            className="filters-right"
            style={{ display: "flex", gap: "0.5rem" }}
          >
            <button
              className="btn-sort"
              onClick={() => handleSort("createdAt")}
              style={{
                padding: "0.3rem 0.6rem",
                background: "none",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.7rem",
              }}
            >
              Date <FontAwesomeIcon icon={getSortIcon("createdAt")} />
            </button>
            <button
              className="btn-sort"
              onClick={() => handleSort("rating")}
              style={{
                padding: "0.3rem 0.6rem",
                background: "none",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.7rem",
              }}
            >
              Rating <FontAwesomeIcon icon={getSortIcon("rating")} />
            </button>
            <button
              className="btn-sort"
              onClick={() => handleSort("helpfulCount")}
              style={{
                padding: "0.3rem 0.6rem",
                background: "none",
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.7rem",
              }}
            >
              Helpful <FontAwesomeIcon icon={getSortIcon("helpfulCount")} />
            </button>
          </div>
        </div>

        {/* Messages */}
        {message.text && (
          <div
            className={`alert-message ${message.type}`}
            style={{
              padding: "0.75rem 1rem",
              borderRadius: "8px",
              marginBottom: "1rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              background: message.type === "success" ? "#dcfce7" : "#fee2e2",
              color: message.type === "success" ? "#16a34a" : "#dc2626",
              borderLeft: `4px solid ${message.type === "success" ? "#16a34a" : "#dc2626"}`,
            }}
          >
            <FontAwesomeIcon
              icon={
                message.type === "success"
                  ? faCheckCircle
                  : faExclamationTriangle
              }
            />
            {message.text}
          </div>
        )}

        {/* Testimonials List */}
        <div className={`testimonials-list ${viewMode}`}>
          {sortedTestimonials.length === 0 ? (
            <div
              className="empty-testimonials"
              style={{
                textAlign: "center",
                padding: "3rem 1.5rem",
                color: "#5A5A5A",
              }}
            >
              <FontAwesomeIcon
                icon={faQuoteLeft}
                size="3x"
                style={{
                  color: "#D9A321",
                  opacity: "0.5",
                  marginBottom: "1rem",
                }}
              />
              <h3 style={{ color: "#1F1F1F", marginBottom: "0.5rem" }}>
                No Testimonials Found
              </h3>
              <p style={{ color: "#5A5A5A" }}>
                Testimonials from users will appear here for moderation.
              </p>
            </div>
          ) : (
            sortedTestimonials.map((testimonial) => {
              const statusStyle = getStatusBadge(testimonial.status);
              const isGlobal = testimonial.origin === "Globally Sourced";

              return (
                <div
                  key={testimonial._id}
                  className={`testimonial-card ${testimonial.isFeatured ? "featured" : ""}`}
                  style={{
                    background: "#ffffff",
                    borderRadius: "12px",
                    padding: "1.25rem",
                    marginBottom: "1rem",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    border: testimonial.isFeatured
                      ? "2px solid #D9A321"
                      : "1px solid #e5e5e5",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div
                    className="testimonial-header"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "0.75rem",
                      marginBottom: "0.75rem",
                    }}
                  >
                    <div
                      className="testimonial-user"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                      }}
                    >
                      <div
                        className="user-avatar"
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          background: "#F8F8F8",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#D9A321",
                          fontSize: "1.1rem",
                        }}
                      >
                        {testimonial.userAvatar ? (
                          <img
                            src={testimonial.userAvatar}
                            alt={testimonial.userName}
                            style={{
                              width: "100%",
                              height: "100%",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <FontAwesomeIcon icon={faUser} />
                        )}
                      </div>
                      <div>
                        <div
                          className="user-name"
                          style={{
                            fontWeight: "600",
                            color: "#1F1F1F",
                            fontSize: "0.875rem",
                          }}
                        >
                          {testimonial.userName || "Anonymous"}
                          {isGlobal && (
                            <span
                              className="origin-tag"
                              style={{
                                display: "inline-block",
                                marginLeft: "0.25rem",
                                fontSize: "0.55rem",
                                background: "#e8f5e9",
                                color: "#006B2E",
                                padding: "0.05rem 0.4rem",
                                borderRadius: "8px",
                              }}
                            >
                              <FontAwesomeIcon icon={faGlobe} /> Globally
                              Sourced
                            </span>
                          )}
                        </div>
                        {testimonial.userEmail && (
                          <div
                            className="user-email"
                            style={{ fontSize: "0.65rem", color: "#5A5A5A" }}
                          >
                            <FontAwesomeIcon icon={faEnvelope} />{" "}
                            {testimonial.userEmail}
                          </div>
                        )}
                        <div
                          className="user-type"
                          style={{ fontSize: "0.6rem", color: "#5A5A5A" }}
                        >
                          {testimonial.userType === "farmer" ? (
                            <>
                              <FontAwesomeIcon icon={faTractor} /> Farmer
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faStore} /> Customer
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      className="testimonial-actions-admin"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        className="testimonial-status"
                        style={{
                          background: statusStyle.bg,
                          color: statusStyle.color,
                          padding: "0.15rem 0.5rem",
                          borderRadius: "12px",
                          fontSize: "0.65rem",
                          fontWeight: "600",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.25rem",
                        }}
                      >
                        <FontAwesomeIcon icon={statusStyle.icon} />{" "}
                        {statusStyle.text}
                      </span>
                      <div
                        className="action-buttons"
                        style={{ display: "flex", gap: "0.25rem" }}
                      >
                        {testimonial.status === "pending" && (
                          <>
                            <button
                              className="btn-approve"
                              onClick={() => handleApprove(testimonial._id)}
                              title="Approve"
                              style={{
                                padding: "0.2rem 0.4rem",
                                background: "#16a34a",
                                color: "#ffffff",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "0.65rem",
                              }}
                            >
                              <FontAwesomeIcon icon={faCheck} />
                            </button>
                            <button
                              className="btn-reject"
                              onClick={() => handleReject(testimonial._id)}
                              title="Reject"
                              style={{
                                padding: "0.2rem 0.4rem",
                                background: "#dc2626",
                                color: "#ffffff",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "0.65rem",
                              }}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </>
                        )}
                        <button
                          className={`btn-feature ${testimonial.isFeatured ? "active" : ""}`}
                          onClick={() => handleFeature(testimonial._id)}
                          title={
                            testimonial.isFeatured ? "Unfeature" : "Feature"
                          }
                          style={{
                            padding: "0.2rem 0.4rem",
                            background: testimonial.isFeatured
                              ? "#D9A321"
                              : "transparent",
                            color: testimonial.isFeatured
                              ? "#ffffff"
                              : "#5A5A5A",
                            border: "1px solid #e5e5e5",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "0.65rem",
                          }}
                        >
                          <FontAwesomeIcon icon={faThumbtack} />
                        </button>
                        <button
                          className="btn-respond"
                          onClick={() => {
                            setSelectedTestimonial(testimonial);
                            setResponseText(testimonial.adminResponse || "");
                            setShowResponseModal(true);
                          }}
                          title="Respond"
                          style={{
                            padding: "0.2rem 0.4rem",
                            background: "#3b82f6",
                            color: "#ffffff",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "0.65rem",
                          }}
                        >
                          <FontAwesomeIcon icon={faReply} />
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(testimonial._id)}
                          title="Delete"
                          style={{
                            padding: "0.2rem 0.4rem",
                            background: "#dc2626",
                            color: "#ffffff",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "0.65rem",
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="testimonial-body">
                    <div
                      className="testimonial-rating"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <RatingStar rating={testimonial.rating || 5} />
                      <span
                        className="rating-date"
                        style={{ fontSize: "0.65rem", color: "#5A5A5A" }}
                      >
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          style={{ marginRight: "0.25rem" }}
                        />
                        {testimonial.createdAt
                          ? new Date(testimonial.createdAt).toLocaleDateString()
                          : "Recent"}
                      </span>
                    </div>

                    {testimonial.title && (
                      <h3
                        className="testimonial-title"
                        style={{
                          fontSize: "1rem",
                          fontWeight: "600",
                          color: "#006B2E",
                          marginBottom: "0.25rem",
                        }}
                      >
                        {testimonial.title}
                      </h3>
                    )}
                    <p
                      className="testimonial-content"
                      style={{
                        fontSize: "0.875rem",
                        color: "#1F1F1F",
                        lineHeight: "1.6",
                        marginBottom: "0.5rem",
                      }}
                    >
                      {testimonial.content || "No content provided"}
                    </p>

                    {testimonial.productName && (
                      <div
                        className="testimonial-product"
                        style={{
                          fontSize: "0.75rem",
                          color: "#5A5A5A",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faTag}
                          style={{ color: "#D9A321" }}
                        />
                        Product: {testimonial.productName}
                        {isGlobal && (
                          <span
                            className="origin-tag"
                            style={{
                              display: "inline-block",
                              marginLeft: "0.25rem",
                              fontSize: "0.5rem",
                              background: "#e8f5e9",
                              color: "#006B2E",
                              padding: "0.05rem 0.3rem",
                              borderRadius: "8px",
                            }}
                          >
                            <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                          </span>
                        )}
                      </div>
                    )}

                    {testimonial.adminResponse && (
                      <div
                        className="testimonial-response"
                        style={{
                          marginTop: "0.5rem",
                          padding: "0.5rem 0.75rem",
                          background: "#f8f9fa",
                          borderRadius: "8px",
                          borderLeft: "3px solid #D9A321",
                        }}
                      >
                        <div
                          className="response-label"
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: "600",
                            color: "#006B2E",
                            marginBottom: "0.15rem",
                          }}
                        >
                          <FontAwesomeIcon icon={faReplyAll} /> Admin Response:
                        </div>
                        <p
                          style={{
                            fontSize: "0.813rem",
                            color: "#1F1F1F",
                            margin: "0",
                          }}
                        >
                          {testimonial.adminResponse}
                        </p>
                        {testimonial.responseDate && (
                          <div
                            className="response-date"
                            style={{
                              fontSize: "0.55rem",
                              color: "#5A5A5A",
                              marginTop: "0.15rem",
                            }}
                          >
                            {new Date(
                              testimonial.responseDate,
                            ).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div
                    className="testimonial-footer"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "0.75rem",
                      paddingTop: "0.5rem",
                      borderTop: "1px solid #f0f0f0",
                    }}
                  >
                    <div
                      className="testimonial-meta"
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        alignItems: "center",
                      }}
                    >
                      <button
                        className="helpful-btn"
                        onClick={() => handleMarkHelpful(testimonial._id)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "0.7rem",
                          color: "#5A5A5A",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.25rem",
                        }}
                      >
                        <FontAwesomeIcon icon={faThumbsUp} />
                        <span>{testimonial.helpfulCount || 0}</span>
                      </button>
                      {testimonial.isFeatured && (
                        <span
                          className="featured-badge"
                          style={{
                            fontSize: "0.55rem",
                            background: "#D9A321",
                            color: "#ffffff",
                            padding: "0.05rem 0.4rem",
                            borderRadius: "10px",
                          }}
                        >
                          <FontAwesomeIcon icon={faThumbtack} /> Featured
                        </span>
                      )}
                      {isGlobal && (
                        <span
                          className="origin-badge"
                          style={{
                            fontSize: "0.55rem",
                            background: "#dcfce7",
                            color: "#16a34a",
                            padding: "0.05rem 0.4rem",
                            borderRadius: "10px",
                          }}
                        >
                          <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Response Modal */}
        {showResponseModal && selectedTestimonial && (
          <div
            className="modal-overlay"
            onClick={() => setShowResponseModal(false)}
            style={{
              position: "fixed",
              top: "0",
              left: "0",
              right: "0",
              bottom: "0",
              background: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: "1000",
              padding: "1rem",
            }}
          >
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#ffffff",
                borderRadius: "16px",
                maxWidth: "500px",
                width: "100%",
                maxHeight: "95vh",
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "1.25rem 1.5rem",
                  borderBottom: "1px solid #e5e5e5",
                }}
              >
                <h2
                  style={{
                    color: "#006B2E",
                    fontSize: "1.1rem",
                    margin: "0",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <FontAwesomeIcon icon={faReplyAll} /> Respond to Testimonial
                </h2>
                <button
                  className="close-modal"
                  onClick={() => setShowResponseModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "1.25rem",
                    cursor: "pointer",
                    color: "#5A5A5A",
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="modal-body" style={{ padding: "1.5rem" }}>
                <div
                  className="testimonial-preview"
                  style={{
                    marginBottom: "1rem",
                    padding: "0.75rem",
                    background: "#f8f9fa",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    className="preview-header"
                    style={{ fontSize: "0.813rem", marginBottom: "0.25rem" }}
                  >
                    <strong>From:</strong>{" "}
                    {selectedTestimonial.userName || "Anonymous"}
                    <span
                      className="preview-type"
                      style={{
                        marginLeft: "0.5rem",
                        fontSize: "0.7rem",
                        color: "#5A5A5A",
                      }}
                    >
                      {selectedTestimonial.userType === "farmer"
                        ? "🌾 Farmer"
                        : "🛒 Customer"}
                    </span>
                  </div>
                  <div className="preview-content">
                    <strong style={{ fontSize: "0.75rem" }}>
                      Testimonial:
                    </strong>
                    <p
                      style={{
                        fontSize: "0.813rem",
                        color: "#1F1F1F",
                        margin: "0.25rem 0 0",
                      }}
                    >
                      {selectedTestimonial.content || "No content"}
                    </p>
                  </div>
                </div>
                <div className="form-group">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.25rem",
                      fontWeight: "500",
                      fontSize: "0.813rem",
                    }}
                  >
                    Response *
                  </label>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    rows="4"
                    placeholder="Write your response to this testimonial..."
                    className="response-textarea"
                    style={{
                      width: "100%",
                      padding: "0.5rem 0.75rem",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                      fontSize: "0.875rem",
                      fontFamily: "Inter, sans-serif",
                      resize: "vertical",
                      minHeight: "100px",
                    }}
                  />
                </div>
              </div>
              <div
                className="form-actions"
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  justifyContent: "flex-end",
                  padding: "1rem 1.5rem",
                  borderTop: "1px solid #e5e5e5",
                }}
              >
                <button
                  className="btn-secondary"
                  onClick={() => setShowResponseModal(false)}
                  style={{
                    padding: "0.5rem 1.25rem",
                    background: "#f0f0f0",
                    color: "#1F1F1F",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "0.875rem",
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleAdminResponse}
                  disabled={!responseText.trim()}
                  style={{
                    padding: "0.5rem 1.25rem",
                    background: "#D9A321",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "0.875rem",
                    opacity: responseText.trim() ? "1" : "0.6",
                  }}
                >
                  <FontAwesomeIcon icon={faReplyAll} /> Send Response
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // User View (Customer/Farmer)
  return (
    <div className="testimonial-manager user" style={{ padding: "0.5rem 0" }}>
      <div
        className="section-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.75rem",
          marginBottom: "1.25rem",
        }}
      >
        <div
          className="header-left"
          style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
        >
          <FontAwesomeIcon
            icon={faQuoteLeft}
            className="header-icon"
            style={{ color: "#D9A321" }}
          />
          <h2
            style={{
              fontSize: "1.125rem",
              color: "#006B2E",
              fontFamily: "Montserrat, sans-serif",
              margin: "0",
            }}
          >
            My Testimonials
          </h2>
          <span
            className="supplier-badge"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.25rem",
              fontSize: "0.6rem",
              background: "rgba(217,163,33,0.2)",
              color: "#D9A321",
              padding: "0.1rem 0.5rem",
              borderRadius: "12px",
            }}
          >
            <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
          </span>
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowModal(true)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.5rem 1rem",
            background: "#D9A321",
            color: "#ffffff",
            border: "none",
            borderRadius: "8px",
            fontSize: "0.813rem",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          <FontAwesomeIcon icon={faPlus} /> Share Your Experience
        </button>
      </div>

      {/* User Stats */}
      <div
        className="user-stats-mini"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))",
          gap: "0.5rem",
          marginBottom: "1.25rem",
        }}
      >
        <div
          className="stat-mini"
          style={{
            background: "#ffffff",
            padding: "0.5rem",
            borderRadius: "8px",
            textAlign: "center",
            border: "1px solid #e5e5e5",
          }}
        >
          <span
            className="stat-mini-value"
            style={{
              display: "block",
              fontSize: "1.1rem",
              fontWeight: "700",
              color: "#1F1F1F",
            }}
          >
            {testimonials.length}
          </span>
          <span
            className="stat-mini-label"
            style={{ fontSize: "0.6rem", color: "#5A5A5A" }}
          >
            Total
          </span>
        </div>
        <div
          className="stat-mini"
          style={{
            background: "#ffffff",
            padding: "0.5rem",
            borderRadius: "8px",
            textAlign: "center",
            border: "1px solid #e5e5e5",
          }}
        >
          <span
            className="stat-mini-value"
            style={{
              display: "block",
              fontSize: "1.1rem",
              fontWeight: "700",
              color: "#16a34a",
            }}
          >
            {testimonials.filter((t) => t.status === "approved").length}
          </span>
          <span
            className="stat-mini-label"
            style={{ fontSize: "0.6rem", color: "#5A5A5A" }}
          >
            Approved
          </span>
        </div>
        <div
          className="stat-mini"
          style={{
            background: "#ffffff",
            padding: "0.5rem",
            borderRadius: "8px",
            textAlign: "center",
            border: "1px solid #e5e5e5",
          }}
        >
          <span
            className="stat-mini-value"
            style={{
              display: "block",
              fontSize: "1.1rem",
              fontWeight: "700",
              color: "#f59e0b",
            }}
          >
            {testimonials.filter((t) => t.status === "pending").length}
          </span>
          <span
            className="stat-mini-label"
            style={{ fontSize: "0.6rem", color: "#5A5A5A" }}
          >
            Pending
          </span>
        </div>
        <div
          className="stat-mini"
          style={{
            background: "#ffffff",
            padding: "0.5rem",
            borderRadius: "8px",
            textAlign: "center",
            border: "1px solid #e5e5e5",
          }}
        >
          <span
            className="stat-mini-value"
            style={{
              display: "block",
              fontSize: "1.1rem",
              fontWeight: "700",
              color: "#dc2626",
            }}
          >
            {testimonials.filter((t) => t.status === "rejected").length}
          </span>
          <span
            className="stat-mini-label"
            style={{ fontSize: "0.6rem", color: "#5A5A5A" }}
          >
            Rejected
          </span>
        </div>
        <div
          className="stat-mini global"
          style={{
            background: "linear-gradient(135deg, #006B2E, #014421)",
            padding: "0.5rem",
            borderRadius: "8px",
            textAlign: "center",
            color: "#ffffff",
          }}
        >
          <span
            className="stat-mini-value"
            style={{ display: "block", fontSize: "1.1rem", fontWeight: "700" }}
          >
            <FontAwesomeIcon icon={faGlobe} />
          </span>
          <span
            className="stat-mini-label"
            style={{ fontSize: "0.6rem", opacity: "0.8" }}
          >
            Globally Sourced
          </span>
        </div>
      </div>

      {message.text && (
        <div
          className={`alert-message ${message.type}`}
          style={{
            padding: "0.75rem 1rem",
            borderRadius: "8px",
            marginBottom: "1rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            background: message.type === "success" ? "#dcfce7" : "#fee2e2",
            color: message.type === "success" ? "#16a34a" : "#dc2626",
            borderLeft: `4px solid ${message.type === "success" ? "#16a34a" : "#dc2626"}`,
          }}
        >
          <FontAwesomeIcon
            icon={
              message.type === "success" ? faCheckCircle : faExclamationTriangle
            }
          />
          {message.text}
        </div>
      )}

      {testimonials.length === 0 ? (
        <div
          className="empty-testimonials"
          style={{
            textAlign: "center",
            padding: "3rem 1.5rem",
            color: "#5A5A5A",
            background: "#ffffff",
            borderRadius: "12px",
            border: "2px dashed #e5e5e5",
          }}
        >
          <FontAwesomeIcon
            icon={faQuoteLeft}
            size="3x"
            style={{ color: "#D9A321", opacity: "0.5", marginBottom: "1rem" }}
          />
          <h3 style={{ color: "#1F1F1F", marginBottom: "0.5rem" }}>
            No Testimonials Yet
          </h3>
          <p style={{ color: "#5A5A5A", marginBottom: "1.5rem" }}>
            Share your experience with our premium globally sourced products and
            help other farmers and customers make informed decisions.
          </p>
          <button
            className="btn-primary"
            onClick={() => setShowModal(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.625rem 1.25rem",
              background: "#D9A321",
              color: "#ffffff",
              border: "none",
              borderRadius: "8px",
              fontSize: "0.875rem",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Write Your First Testimonial
          </button>
        </div>
      ) : (
        <div className="testimonials-list user-list">
          {testimonials.map((testimonial) => {
            const statusStyle = getStatusBadge(testimonial.status);
            const isGlobal = testimonial.origin === "Globally Sourced";

            return (
              <div
                key={testimonial._id}
                className={`testimonial-card ${testimonial.status}`}
                style={{
                  background: "#ffffff",
                  borderRadius: "12px",
                  padding: "1.25rem",
                  marginBottom: "1rem",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  border: "1px solid #e5e5e5",
                }}
              >
                <div
                  className="testimonial-header"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "0.75rem",
                    marginBottom: "0.75rem",
                  }}
                >
                  <div
                    className="testimonial-user"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                    }}
                  >
                    <div
                      className="user-avatar"
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        background: "#F8F8F8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#D9A321",
                        fontSize: "1.1rem",
                      }}
                    >
                      <FontAwesomeIcon icon={userTypeIcon} />
                    </div>
                    <div>
                      <div
                        className="user-name"
                        style={{
                          fontWeight: "600",
                          color: "#1F1F1F",
                          fontSize: "0.875rem",
                        }}
                      >
                        {testimonial.userName || user?.name || "Anonymous"}
                        {isGlobal && (
                          <span
                            className="origin-tag"
                            style={{
                              display: "inline-block",
                              marginLeft: "0.25rem",
                              fontSize: "0.55rem",
                              background: "#e8f5e9",
                              color: "#006B2E",
                              padding: "0.05rem 0.4rem",
                              borderRadius: "8px",
                            }}
                          >
                            <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                          </span>
                        )}
                      </div>
                      <div
                        className="user-type"
                        style={{ fontSize: "0.6rem", color: "#5A5A5A" }}
                      >
                        {user?.userType === "farmer" ? (
                          <>
                            <FontAwesomeIcon icon={faTractor} /> Farmer
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faStore} /> Customer
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <span
                    className="testimonial-status"
                    style={{
                      background: statusStyle.bg,
                      color: statusStyle.color,
                      padding: "0.15rem 0.5rem",
                      borderRadius: "12px",
                      fontSize: "0.65rem",
                      fontWeight: "600",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                  >
                    <FontAwesomeIcon icon={statusStyle.icon} />{" "}
                    {statusStyle.text}
                  </span>
                </div>

                <div className="testimonial-body">
                  <div
                    className="testimonial-rating"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <RatingStar rating={testimonial.rating || 5} />
                    <span
                      className="rating-date"
                      style={{ fontSize: "0.65rem", color: "#5A5A5A" }}
                    >
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        style={{ marginRight: "0.25rem" }}
                      />
                      {testimonial.createdAt
                        ? new Date(testimonial.createdAt).toLocaleDateString()
                        : "Recent"}
                    </span>
                  </div>

                  {testimonial.title && (
                    <h3
                      className="testimonial-title"
                      style={{
                        fontSize: "1rem",
                        fontWeight: "600",
                        color: "#006B2E",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {testimonial.title}
                    </h3>
                  )}
                  <p
                    className="testimonial-content"
                    style={{
                      fontSize: "0.875rem",
                      color: "#1F1F1F",
                      lineHeight: "1.6",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {testimonial.content || "No content"}
                  </p>

                  {testimonial.productName && (
                    <div
                      className="testimonial-product"
                      style={{
                        fontSize: "0.75rem",
                        color: "#5A5A5A",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faTag}
                        style={{ color: "#D9A321" }}
                      />
                      Product: {testimonial.productName}
                      {isGlobal && (
                        <span
                          className="origin-tag"
                          style={{
                            display: "inline-block",
                            marginLeft: "0.25rem",
                            fontSize: "0.5rem",
                            background: "#e8f5e9",
                            color: "#006B2E",
                            padding: "0.05rem 0.3rem",
                            borderRadius: "8px",
                          }}
                        >
                          <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                        </span>
                      )}
                    </div>
                  )}

                  {testimonial.status === "rejected" &&
                    testimonial.adminResponse && (
                      <div
                        className="testimonial-feedback rejected"
                        style={{
                          marginTop: "0.5rem",
                          padding: "0.5rem 0.75rem",
                          background: "#fee2e2",
                          borderRadius: "8px",
                          borderLeft: "3px solid #dc2626",
                        }}
                      >
                        <div
                          className="feedback-label"
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: "600",
                            color: "#dc2626",
                            marginBottom: "0.15rem",
                          }}
                        >
                          <FontAwesomeIcon icon={faExclamationTriangle} /> Admin
                          Feedback:
                        </div>
                        <p
                          style={{
                            fontSize: "0.813rem",
                            color: "#1F1F1F",
                            margin: "0",
                          }}
                        >
                          {testimonial.adminResponse}
                        </p>
                      </div>
                    )}

                  {testimonial.status === "approved" &&
                    testimonial.adminResponse && (
                      <div
                        className="testimonial-response"
                        style={{
                          marginTop: "0.5rem",
                          padding: "0.5rem 0.75rem",
                          background: "#f8f9fa",
                          borderRadius: "8px",
                          borderLeft: "3px solid #D9A321",
                        }}
                      >
                        <div
                          className="response-label"
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: "600",
                            color: "#006B2E",
                            marginBottom: "0.15rem",
                          }}
                        >
                          <FontAwesomeIcon icon={faReplyAll} /> Response from
                          Kenali Enterprise:
                        </div>
                        <p
                          style={{
                            fontSize: "0.813rem",
                            color: "#1F1F1F",
                            margin: "0",
                          }}
                        >
                          {testimonial.adminResponse}
                        </p>
                      </div>
                    )}
                </div>

                <div
                  className="testimonial-footer"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "0.75rem",
                    paddingTop: "0.5rem",
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <div
                    className="testimonial-actions"
                    style={{ display: "flex", gap: "0.5rem" }}
                  >
                    {testimonial.status === "pending" && (
                      <>
                        <button
                          className="btn-edit"
                          onClick={() => openEditModal(testimonial)}
                          style={{
                            padding: "0.2rem 0.5rem",
                            background: "#D9A321",
                            color: "#ffffff",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "0.65rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.2rem",
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} /> Edit
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(testimonial._id)}
                          style={{
                            padding: "0.2rem 0.5rem",
                            background: "#dc2626",
                            color: "#ffffff",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "0.65rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.2rem",
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} /> Delete
                        </button>
                      </>
                    )}
                  </div>
                  <div
                    className="testimonial-helpful"
                    style={{
                      fontSize: "0.7rem",
                      color: "#5A5A5A",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                  >
                    <FontAwesomeIcon icon={faThumbsUp} />
                    <span>{testimonial.helpfulCount || 0}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowModal(false)}
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: "1000",
            padding: "1rem",
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              maxWidth: "500px",
              width: "100%",
              maxHeight: "95vh",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
          >
            <div
              className="modal-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1.25rem 1.5rem",
                borderBottom: "1px solid #e5e5e5",
              }}
            >
              <h2
                style={{
                  color: "#006B2E",
                  fontSize: "1.1rem",
                  margin: "0",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                {editingTestimonial ? (
                  <>
                    <FontAwesomeIcon icon={faEdit} /> Edit Your Testimonial
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlus} /> Share Your Experience
                  </>
                )}
                <span
                  className="supplier-badge"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.25rem",
                    fontSize: "0.55rem",
                    background: "rgba(217,163,33,0.2)",
                    color: "#D9A321",
                    padding: "0.1rem 0.4rem",
                    borderRadius: "12px",
                  }}
                >
                  <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                </span>
              </h2>
              <button
                className="close-modal"
                onClick={() => setShowModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.25rem",
                  cursor: "pointer",
                  color: "#5A5A5A",
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="testimonial-form"
              style={{ padding: "1.5rem" }}
            >
              <div className="form-group" style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.25rem",
                    fontWeight: "500",
                    fontSize: "0.813rem",
                  }}
                >
                  Rating *
                </label>
                <div
                  className="rating-input"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                >
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`rating-star ${formData.rating >= star ? "active" : ""}`}
                      onClick={() => setFormData({ ...formData, rating: star })}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "1.5rem",
                        color: formData.rating >= star ? "#D9A321" : "#e5e5e5",
                      }}
                    >
                      <FontAwesomeIcon icon={faStar} />
                    </button>
                  ))}
                  <span
                    className="rating-label"
                    style={{
                      marginLeft: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      color: "#1F1F1F",
                    }}
                  >
                    {formData.rating} / 5
                  </span>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.25rem",
                    fontWeight: "500",
                    fontSize: "0.813rem",
                  }}
                >
                  Title *
                </label>
                <input
                  type="text"
                  placeholder="e.g., Excellent product, highly recommend!"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  required
                  maxLength="100"
                  className="form-input"
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                  }}
                />
                <small
                  className="char-count"
                  style={{
                    display: "block",
                    fontSize: "0.6rem",
                    color: "#5A5A5A",
                    marginTop: "0.15rem",
                    textAlign: "right",
                  }}
                >
                  {formData.title.length}/100
                </small>
              </div>

              <div className="form-group" style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.25rem",
                    fontWeight: "500",
                    fontSize: "0.813rem",
                  }}
                >
                  Your Experience *
                </label>
                <textarea
                  placeholder="Share your experience with our premium globally sourced products. What did you like? How did it help you?"
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  required
                  rows="5"
                  maxLength="1000"
                  className="form-textarea"
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                    fontFamily: "Inter, sans-serif",
                    resize: "vertical",
                    minHeight: "120px",
                  }}
                />
                <small
                  className="char-count"
                  style={{
                    display: "block",
                    fontSize: "0.6rem",
                    color: "#5A5A5A",
                    marginTop: "0.15rem",
                    textAlign: "right",
                  }}
                >
                  {formData.content.length}/1000
                </small>
              </div>

              <div className="form-group" style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.25rem",
                    fontWeight: "500",
                    fontSize: "0.813rem",
                  }}
                >
                  Product Name (Optional)
                </label>
                <input
                  type="text"
                  placeholder="Which product are you reviewing?"
                  value={formData.productName}
                  onChange={(e) =>
                    setFormData({ ...formData, productName: e.target.value })
                  }
                  className="form-input"
                  style={{
                    width: "100%",
                    padding: "0.5rem 0.75rem",
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    fontSize: "0.875rem",
                  }}
                />
              </div>

              <div
                className="supplier-note"
                style={{
                  padding: "0.5rem 0.75rem",
                  background: "#e8f5e9",
                  borderRadius: "8px",
                  fontSize: "0.75rem",
                  color: "#006B2E",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                  marginBottom: "1rem",
                  borderLeft: "3px solid #D9A321",
                }}
              >
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  style={{ color: "#D9A321", marginTop: "0.1rem" }}
                />
                <span>
                  All products are <strong>globally sourced</strong> from
                  premium suppliers, ensuring the highest quality standards.
                </span>
              </div>

              <div
                className="form-actions"
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  style={{
                    padding: "0.5rem 1.25rem",
                    background: "#f0f0f0",
                    color: "#1F1F1F",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "0.875rem",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                  style={{
                    padding: "0.5rem 1.25rem",
                    background: "#D9A321",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: "600",
                    fontSize: "0.875rem",
                    opacity: submitting ? "0.6" : "1",
                  }}
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Submitting...
                    </>
                  ) : editingTestimonial ? (
                    <>
                      <FontAwesomeIcon icon={faEdit} /> Update
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlus} /> Submit Testimonial
                    </>
                  )}
                </button>
              </div>
            </form>

            <div
              className="modal-footer-note"
              style={{
                padding: "0.75rem 1.5rem",
                borderTop: "1px solid #e5e5e5",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.7rem",
                color: "#5A5A5A",
              }}
            >
              <FontAwesomeIcon
                icon={faInfoCircle}
                style={{ color: "#D9A321" }}
              />
              <small>
                Your testimonial will be reviewed before being published. Thank
                you for sharing!
              </small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialManager;
